import { Navtex, VesselRs1345ModelBase, NavtexStation, BroadcastType } from './vessel-rs1345-model';
import { Rs1345UpdateInfo, Rs1345Service } from '../services/rs1345-service';
import { getLatestWeekDate } from '../utils/helpers';
import circle from "@turf/circle";
import { RouteGeometry } from './voyage-routing-model';
import { CrossedGeometries } from '../utils/geometries-intersection';

const NmToKm = 1.852;

export class VesselRs1345ModelImpl extends VesselRs1345ModelBase {
    async getHtml(address: string){
        try {
            let html: string = await this.getHtmlFromService(address);
            const regex = /<body[^>]*>((.|[\n\r])*)<\/body>/;
            const results = regex.exec(html);
            
            if (results) {
                this.dispatchEvent(new CustomEvent('fetch-html', {detail: { html: results[1], address: address }}));
            }
        } catch(error) {
            console.warn(error);
        }
    }

    async getHtmlFromService(address: string) {
        const html = await Rs1345Service.getHtml(address);
        return html;
    }

    private _internationalStationsFilter(n: number): boolean {
        return n === 518000;
    }

    getWeatherStationsCrossingRoute(route: RouteGeometry): string {
        return this._getStationsCrossingRoute(route, "weather bulletins");
    }

    getNavWarningStationsCrossingRoute(route: RouteGeometry): string {
        return this._getStationsCrossingRoute(route, "navigational warnings");
    }

    async getStations(): Promise<NavtexStation[]> {
        await this._navTexFetchPromise;
        return this._navTexStations || [];
    }

    private _getStationsCrossingRoute(route: RouteGeometry, broadcastType: BroadcastType): string {
        if (!this._navTexStations) {
            return "";
        }

        const stations = this._navTexStations.filter(ntx => ntx.broadcasts.find(b => b === broadcastType));
        const crossedStations = CrossedGeometries(route, stations);

        const result: string[] = [];

        crossedStations.forEach(station => {
            const text = `["${station.designator}"] ${station.address.replace(`NAVTEX / `, '')}`;
            result.push(text);
        });

        return result.sort().join('\n');
    }

    private async _getNavtex() {
        const navtex = await Rs1345Service.getNavtex();
        this._navTex = navtex;
        this._navTexStations = this._extractNavtexStations(this._navTex);
    }

    private _extractNavtexStations(navtex: Navtex[]): NavtexStation[] {
        let navtexStations: NavtexStation[] = [];

        navtex?.forEach(ntx => {
            try {
                const service = ntx.services.find(s => this._internationalStationsFilter(s.frequencyHz));
                if (service && service.rangeNM && !isNaN(service.rangeNM)) {
                    navtexStations.push({
                        address: ntx.address,
                        broadcasts: service.broadcasts,
                        designator: service.designator,
                        frequencyHz: service.frequencyHz,
                        name: ntx.name,
                        position: ntx.position,
                        geometry: {
                            coordinates: circle(ntx.position.coordinates, service.rangeNM! * NmToKm, {steps: 500, units: 'kilometers'}).geometry.coordinates,
                            type: "Polygon"
                        },
                        rangeNM: service.rangeNM,
                    })
                }
            } catch (error) {
                console.warn(`Navtex station ${ntx.name} contains invalid data\n${error}`);
            }
        });

        return navtexStations;
    }

    public get latestUpdateDate() {
        return this._latestUpdateDate;
    }

    constructor(navtex?: Navtex[]) {
        super();

        if (navtex) {
            this._navTex = navtex;
            this._navTexStations = this._extractNavtexStations(navtex);
        } else {
              // if local development we will not be able to get navtex
            if( (<any>window)["appenv"].isLocal) {
                return
            }
            this.getLatestUpdateDate().then(date => this._latestUpdateDate = date);
            this._navTexFetchPromise = this._getNavtex();

            setInterval(this._updateLatestUpdateDate, 60*60*1000); // update every hour
            setInterval(this._getNavtex, 60*60*1000); // update every hour
        }
    }

    async getUpdateInfo(): Promise<Map<string, Rs1345UpdateInfo>> {
        const updateInfo = await Rs1345Service.getUpdateInfo();
        return updateInfo;
    }

    async getLatestUpdateDate() : Promise<string> {
        const updates: Map<string, Rs1345UpdateInfo> = await this.getUpdateInfo();
        const values = Array.from(updates.values());
        const result = getLatestWeekDate(values.filter(u => u.licensed && u.week !== null).map(u => u.week!));
        
        if (result) {
            return result;
        }

        return "";
    }

    private _latestUpdateDate: string | null = null;
    private _navTex: Navtex[] | null = null;
    private _navTexFetchPromise: Promise<void> | null = null;
    private _navTexStations: NavtexStation[]|null = null;

    private async _updateLatestUpdateDate() {
        const date = await this.getLatestUpdateDate();
        this._latestUpdateDate = date;
    }
}
